<script setup>
import { router, useForm } from '@inertiajs/vue3';
import { computed, ref, getCurrentInstance } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import Dialog from 'primevue/dialog';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import { useLoading } from '@/composables/useLoading';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const confirm = useConfirm();
const toast = useToast();
const { showLoading, hideLoading } = useLoading();

const props = defineProps({
    title: String,
    blacklist: Object,
    filters: Object,
});

const blacklistData = computed(() => props.blacklist.data || []);

const loading = ref(false);

const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString();
};

// Add to blacklist dialog
const addDialog = ref(false);

const addForm = useForm({
    url: '',
    reason: '',
});

const openAddDialog = () => {
    addForm.reset();
    addForm.clearErrors();
    addDialog.value = true;
};

const submitAdd = () => {
    addForm.post(route('site-blacklist.store'), {
        preserveScroll: true,
        onStart: () => showLoading($t('Adding to blacklist...')),
        onFinish: () => hideLoading(),
        onSuccess: () => {
            addDialog.value = false;
            addForm.reset();
            toast.add({ severity: 'success', summary: $t('Success'), detail: $t('Added to blacklist successfully'), life: 3000 });
        },
        onError: () => {
            toast.add({ severity: 'error', summary: $t('Error'), detail: $t('Failed to add to blacklist'), life: 3000 });
        }
    });
};

// Unblock
const unblock = (row) => {
    confirm.require({
        message: `${$t('Are you sure you want to unblock')} ${row.url}?`,
        header: $t('Unblock'),
        icon: 'pi pi-exclamation-triangle',
        rejectClass: 'p-button-secondary p-button-outlined',
        acceptClass: 'p-button-danger',
        accept: () => {
            router.delete(route('site-blacklist.destroy', row.id), {
                preserveScroll: true,
                onStart: () => showLoading($t('Unblocking...')),
                onFinish: () => hideLoading(),
                onSuccess: () => {
                    toast.add({ severity: 'success', summary: $t('Success'), detail: $t('Unblocked successfully'), life: 3000 });
                },
                onError: () => {
                    toast.add({ severity: 'error', summary: $t('Error'), detail: $t('Failed to unblock'), life: 3000 });
                }
            });
        }
    });
};

const fetchBlacklist = (page = null) => {
    const params = {};

    if (page) {
        params.page = page;
    }

    router.get(route('site-blacklist.index'), params, {
        preserveState: true,
        preserveScroll: true,
        onStart: () => showLoading($t('Loading blacklist...')),
        onFinish: () => hideLoading()
    });
};

const onPage = (event) => {
    fetchBlacklist(event.page + 1);
};

const paginatorTemplate = {
    layout: 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport',
    CurrentPageReport: $t('Showing {first} to {last} of {totalRecords} entries')
};
</script>

<template>
    <AdminMainLayout>
        <ConfirmDialog />

        <div class="flex justify-between items-center mb-4">
            <h2 class="text-2xl font-semibold text-gray-900">{{ $t('Blacklist') }}</h2>
            <Button :label="$t('Add to blacklist')" icon="pi pi-plus" class="p-button-success" @click="openAddDialog" />
        </div>

        <DataTable
            :value="blacklistData"
            :paginator="true"
            :rows="30"
            :totalRecords="blacklist.total"
            :lazy="true"
            @page="onPage"
            :loading="loading"
            dataKey="id"
            responsiveLayout="scroll"
            :paginatorTemplate="paginatorTemplate.layout"
            :currentPageReportTemplate="$t('Showing {first} to {last} of {totalRecords} entries')"
            class="p-datatable-sm">

            <template #empty>
                <div class="text-center py-4">{{ $t('No entries found.') }}</div>
            </template>

            <Column field="url" :header="$t('Domain')" style="min-width: 220px">
                <template #body="slotProps">
                    <span class="font-medium">{{ slotProps.data.url }}</span>
                </template>
            </Column>

            <Column field="reason" :header="$t('Reason')" style="min-width: 220px">
                <template #body="slotProps">
                    <span>{{ slotProps.data.reason || '-' }}</span>
                </template>
            </Column>

            <Column :header="$t('Added by')" style="min-width: 150px">
                <template #body="slotProps">
                    <span>{{ slotProps.data.creator?.name || '-' }}</span>
                </template>
            </Column>

            <Column field="created_at" :header="$t('Date')" style="min-width: 160px">
                <template #body="slotProps">
                    <span>{{ formatDate(slotProps.data.created_at) }}</span>
                </template>
            </Column>

            <Column :header="$t('Actions')" :exportable="false" style="min-width: 120px">
                <template #body="slotProps">
                    <div class="flex gap-2">
                        <Button icon="pi pi-trash" class="p-button-rounded p-button-text p-button-danger"
                            @click="unblock(slotProps.data)" v-tooltip.top="$t('Unblock')" />
                    </div>
                </template>
            </Column>
        </DataTable>

        <!-- Add to blacklist Dialog -->
        <Dialog v-model:visible="addDialog" modal :header="$t('Add to blacklist')" :style="{ width: '95vw', maxWidth: '32rem' }" :breakpoints="{ '641px': '95vw' }">
            <form @submit.prevent="submitAdd">
                <div class="field mb-4">
                    <label for="bl-url" class="font-semibold block mb-2">{{ $t('Domain') }} <span class="text-red-500">*</span></label>
                    <InputText
                        id="bl-url"
                        v-model="addForm.url"
                        class="w-full"
                        :class="{ 'p-invalid': addForm.errors.url }"
                        :placeholder="$t('Domain')"
                        :disabled="addForm.processing" />
                    <small v-if="addForm.errors.url" class="p-error block mt-1">{{ addForm.errors.url }}</small>
                </div>

                <div class="field mb-2">
                    <label for="bl-reason" class="font-semibold block mb-2">{{ $t('Reason (optional)') }}</label>
                    <Textarea
                        id="bl-reason"
                        v-model="addForm.reason"
                        class="w-full"
                        rows="3"
                        :class="{ 'p-invalid': addForm.errors.reason }"
                        :disabled="addForm.processing" />
                    <small v-if="addForm.errors.reason" class="p-error block mt-1">{{ addForm.errors.reason }}</small>
                </div>
            </form>

            <template #footer>
                <Button :label="$t('Cancel')" icon="pi pi-times" class="p-button-secondary" @click="addDialog = false" :disabled="addForm.processing" />
                <Button :label="$t('Save')" icon="pi pi-check" class="p-button-success" @click="submitAdd" :loading="addForm.processing" :disabled="addForm.processing" />
            </template>
        </Dialog>
    </AdminMainLayout>
</template>

<style scoped>
.card {
    background: white;
    padding: 1.5rem;
    border-radius: 0.5rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}

.p-error {
    color: var(--red-500);
    font-size: 0.875rem;
}
</style>
